/**
 * lib/employerReviewerStrings.js — Phase 1.0 reviewer UI string table.
 *
 * Centralised so a future i18n migration can swap this constant
 * for `useTranslation()` lookups with a mechanical refactor. Keys
 * are stable; values are en-GB.
 */
export const REVIEWER_STRINGS = {
  /* Reviewer queue page */
  'reviewer.queue.title':              'My reviews',
  'reviewer.queue.subtitle':           'Candidates routed to you for review. Start a pending review, then advance, reject, or place on hold. All decisions are append-only.',
  'reviewer.queue.counter.pending':    'Pending',
  'reviewer.queue.counter.in_review':  'In review',
  'reviewer.queue.counter.decided':    'Decided',
  'reviewer.queue.section.title':      'Queue',
  'reviewer.queue.empty':              'No reviews assigned yet.',
  'reviewer.queue.empty.body':         'When an admin or recruiter routes a candidate to you, the review will appear here as \u201CPending\u201D. Start it to lock the review-in-progress state, then decide (advance, reject, or place on hold). All decisions are append-only.',
  'reviewer.queue.empty.cta':          'Back to Organisation',
  'reviewer.queue.loading':            'Loading\u2026',
  'reviewer.queue.col.review':         'Review',
  'reviewer.queue.col.status':         'Status',
  'reviewer.queue.col.decision':       'Decision',
  'reviewer.queue.col.routed':         'Routed',
  'reviewer.queue.col.actions':        'Actions',
  'reviewer.queue.action.start':       'Start',
  'reviewer.queue.action.decide':      'Decide',
  'reviewer.queue.action.note':        'Note',

  /* Decide dialog */
  'reviewer.decide.title':             'Record decision',
  'reviewer.decide.field.decision':    'Decision',
  'reviewer.decide.option.advance':    'Advance',
  'reviewer.decide.option.reject':     'Reject',
  'reviewer.decide.option.on_hold':    'On hold',
  'reviewer.decide.field.rationale':   'Rationale',
  'reviewer.decide.rationale.required':'Required when rejecting',
  'reviewer.decide.rationale.optional':'Optional context (max 2000 chars)',
  'reviewer.decide.cancel':            'Cancel',
  'reviewer.decide.submit':            'Submit decision',
  'reviewer.decide.toast.success':     'Decision recorded. Your recommendation will be considered by the authorised hiring team before the candidate’s status is changed.',
  'reviewer.decide.toast.fail':        'Failed to record decision',
  'reviewer.decide.rationale.warning': 'A rationale is required for rejection',

  /* Note dialog */
  'reviewer.note.title':               'Add note',
  'reviewer.note.placeholder':         'Notes are append-only and visible to admins/recruiters.',
  'reviewer.note.cancel':              'Cancel',
  'reviewer.note.submit':              'Add note',
  'reviewer.note.toast.success':       'Note added',
  'reviewer.note.toast.fail':          'Failed to add note',

  /* Generic */
  'reviewer.toast.start.success':      'Review started',
  'reviewer.toast.start.fail':         'Failed to start review',
  'reviewer.toast.queue.fail':         'Failed to load reviewer queue',
  'reviewer.status.pending':           'Pending',
  'reviewer.status.in_review':         'In review',
  'reviewer.status.decided':           'Decided',
  'reviewer.decision.advance':         'Advance',
  'reviewer.decision.reject':          'Reject',
  'reviewer.decision.on_hold':         'On hold',

  /* Admin roster page */
  'admin.roster.title':                'Reviewer roster',
  'admin.roster.subtitle':             'Assign reviewers to roles, pause or resume their access, and remove assignments. Reviewers see only their own queue.',
  'admin.roster.access.denied':        'You need an admin or recruiter role to manage reviewer assignments.',
  'admin.roster.bootstrap.fail':       'Failed to load reviewer roster',
  'admin.roster.loading':              'Loading reviewer roster\u2026',
  'admin.roster.empty':                'No reviewer assignments yet.',
  'admin.roster.empty.body':           'Reviewers are members with the \u201Creviewer\u201D role on this account. Once you\u2019ve invited them and created a role, use \u201CAssign reviewer\u201D to bind them to that role. From then on, you can route candidates from the Matches surface to all active reviewers in one click.',
  'admin.roster.empty.cta.members':    'Invite reviewers',
  'admin.roster.empty.cta.roles':      'Manage roles',
  'admin.roster.action.assign':        'Assign reviewer',
  'admin.roster.action.refresh':       'Refresh',
  'admin.roster.col.role':             'Role',
  'admin.roster.col.reviewer':         'Reviewer',
  'admin.roster.col.status':           'Status',
  'admin.roster.col.created':          'Assigned',
  'admin.roster.col.actions':          'Actions',
  'admin.roster.status.active':        'Active',
  'admin.roster.status.paused':        'Paused',
  'admin.roster.status.removed':       'Removed',
  'admin.roster.action.pause':         'Pause',
  'admin.roster.action.resume':        'Resume',
  'admin.roster.action.unassign':      'Unassign',
  'admin.roster.toast.pause.success':  'Reviewer assignment paused',
  'admin.roster.toast.pause.fail':     'Failed to pause assignment',
  'admin.roster.toast.resume.success': 'Reviewer assignment resumed',
  'admin.roster.toast.resume.fail':    'Failed to resume assignment',
  'admin.roster.toast.unassign.success':'Reviewer unassigned',
  'admin.roster.toast.unassign.fail':  'Failed to unassign reviewer',
  'admin.roster.unassign.confirm':     'Remove this reviewer\u2019s assignment? This cannot be undone but is auditable.',

  /* Assign dialog */
  'admin.assign.title':                'Assign reviewer',
  'admin.assign.field.role':           'Role',
  'admin.assign.field.reviewer':       'Reviewer',
  'admin.assign.placeholder.role':     'Select a role\u2026',
  'admin.assign.placeholder.reviewer': 'Select a reviewer\u2026',
  'admin.assign.help.reviewer':        'Only employer members with the \u201Creviewer\u201D role appear here. Invite reviewers from the Members page first.',
  'admin.assign.cancel':               'Cancel',
  'admin.assign.submit':               'Assign',
  'admin.assign.toast.success':        'Reviewer assigned',
  'admin.assign.toast.fail':           'Failed to assign reviewer',
  'admin.assign.no_roles':             'No roles available. Create a role first.',
  'admin.assign.no_reviewers':         'No members with the reviewer role yet.',

  /* Reviewer coverage panel (Role Detail) */
  'coverage.title':                    'Reviewer coverage',
  'coverage.loading':                  'Loading reviewer coverage\u2026',
  'coverage.fail':                     'Failed to load reviewer coverage',
  'coverage.active_assignments':       'Active reviewers',
  'coverage.open.pending':             'Pending reviews',
  'coverage.open.in_review':           'In review',
  'coverage.decided':                  'Decided',
  'coverage.consensus.label':          'Status:',
  'coverage.empty':                    'No reviewers assigned to this role yet.',
  'coverage.cta.assign':               'Assign reviewers',
  'coverage.cta.view_roster':          'View reviewer roster',
  'coverage.help':                     'Open reviews are counted across all reviewers assigned to this role.',

  /* Route-to-reviewers (Role Matches, revealed-match only) */
  'route.action.default':              'Route to reviewers',
  'route.action.routed':               'Routed to reviewers',
  'route.action.already_routed':       'Already routed',
  'route.action.disabled.no_reviewers': 'No active reviewers on this role',
  'route.toast.success':               'Fanned out to {{n}} reviewers',
  'route.toast.cta.view_reviewers':    'View reviewers',
  'route.toast.fail':                  'Failed to route candidate',
  'route.aria.routed_match':           'Route this revealed candidate to all active reviewers',

  /* Workload counters (Reviewer Roster) */
  'admin.roster.col.workload':         'Workload',
  'admin.roster.workload.none':        'No open reviews',
  'admin.roster.workload.pending':     '{{n}} pending',
  'admin.roster.workload.in_review':   '{{n}} in review',
  'admin.roster.workload.fail':        'Failed to load workload counters',
};

/**
 * Lookup helper. If a key is missing, returns the key itself so the
 * missing-string surfaces visibly in dev.
 */
export const t = (key) => REVIEWER_STRINGS[key] ?? key;

/**
 * Lookup + simple {{token}} interpolation. Used for counter copy
 * (e.g. "Fanned out to {{n}} reviewers"). Missing keys still
 * surface as the key string.
 */
export const tn = (key, vars = {}) => {
  const raw = REVIEWER_STRINGS[key] ?? key;
  return String(raw).replace(/\{\{(\w+)\}\}/g, (m, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : m,
  );
};
